import {
  ConfirmationModal,
  DATE_FORMATTER_STR,
  DateInput,
  Filters,
  formatDate,
  GameInfo,
  Header,
  ScrollContainer,
  Search,
  Section,
  Select,
  StatsTeam,
  Typography,
  useBreakpoints,
  ValueOpt,
} from "best-common-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { deleteDayOfTicketRequest, getDayOfRequests } from "../../../api/RequesTixApi";
import AdminActiveTodaysTicketsTable from "../../../components/todaysTickets/admin/AdminActiveTodaysTicketsTable";
import AdminTicketsRevokeModal from "../../../components/todaysTickets/admin/AdminTicketsRevokeModal";
import RouteConstants, { urlReplace } from "../../../constants/RouteConstants";
import { useDropdowns } from "../../../contexts/DropdownsContext";
import { DayOfGameRequestDTO, DayOfRequestStatus } from "../../../types/TodaysTickets";
import { todayStr } from "../../../util/DateObjUtil";
import { useUrlParams } from "../../../util/RouteUtil";
import { getTeamOption } from "../../../util/TeamUtil";
import { formatSubmittedDate } from "../../../util/TimeUtil";
import TodaysTicketsDataTable, { DAY_OF_REQUEST_STATUS_IDS, DayOfTableData } from "./TodaysTicketsDataTable";

const LOTTERY_STATUS_IDS = [
  DAY_OF_REQUEST_STATUS_IDS.LOTTERY_PENDING,
  DAY_OF_REQUEST_STATUS_IDS.LOTTERY_WON,
  DAY_OF_REQUEST_STATUS_IDS.LOTTERY_LOST,
];
const FCFS_STATUS_IDS = [
  DAY_OF_REQUEST_STATUS_IDS.FORWARDED,
  DAY_OF_REQUEST_STATUS_IDS.REVOKED,
  DAY_OF_REQUEST_STATUS_IDS.FAILED,
  DAY_OF_REQUEST_STATUS_IDS.DELETED,
  DAY_OF_REQUEST_STATUS_IDS.CANCELLED,
  DAY_OF_REQUEST_STATUS_IDS.PENDING_FORWARD,
];
const LOTTERY_REVOKE_STATUS_IDS = [DAY_OF_REQUEST_STATUS_IDS.LOTTERY_WON];
const FCFS_REVOKE_STATUS_IDS = [DAY_OF_REQUEST_STATUS_IDS.FORWARDED, DAY_OF_REQUEST_STATUS_IDS.PENDING_FORWARD];

const ALL_STATUSES_OPTION = { label: "All Statuses", value: undefined } as ValueOpt<DayOfRequestStatus | undefined>;

const includesSearch = (value: unknown, searchTerm: string): boolean => {
  return (value ?? "").toString().toLowerCase().includes(searchTerm);
};

const matchesSearch = (request: DayOfTableData, searchTerm?: string): boolean => {
  const normalized = searchTerm?.trim().toLowerCase();
  if (!normalized) {
    return true;
  }

  const awayTeam = request.game?.teams?.away?.team;
  const homeTeam = request.game?.teams?.home?.team;
  return [
    request.firstName,
    request.lastName,
    request.ballparkEmail,
    request.numberOfTickets,
    request.stadium,
    request.ticketsSentDisplay,
    request.status,
    request.requestTime,
    request.game?.gameDate,
    awayTeam?.name,
    awayTeam?.abbreviation,
    homeTeam?.name,
    homeTeam?.abbreviation,
  ].some((value) => includesSearch(value, normalized));
};

const AdminTodaysTickets = () => {
  const { teamsOptions, dayOfRequestStatusOptions } = useDropdowns();
  const { isMobile } = useBreakpoints();
  const [requestsLoading, setRequestsLoading] = useState<boolean>(false);
  const [requests, setRequests] = useState<DayOfTableData[]>([]);
  const [toBeDeleted, setToBeDeleted] = useState<DayOfTableData | undefined>(undefined);
  const [toBeRevoked, setToBeRevoked] = useState<DayOfTableData | undefined>(undefined);

  const onDeleteClicked = (_index: number, value: DayOfTableData) => {
    setToBeDeleted(value);
  };

  const [{ date, teamId, searchTerm, lotteryStatusId, fcfsStatusId }, setParam] = useUrlParams({
    date: todayStr,
    teamId: undefined,
    searchTerm: "",
    lotteryStatusId: undefined,
    fcfsStatusId: undefined,
  });

  const selectedHomeTeam = useMemo(() => getTeamOption(teamId, teamsOptions), [teamId, teamsOptions]);

  const lotteryStatusOptions = useMemo(
    () => [
      ALL_STATUSES_OPTION,
      ...dayOfRequestStatusOptions.filter((option) => LOTTERY_STATUS_IDS.includes(option.value.dayOfRequestStatusId)),
    ],
    [dayOfRequestStatusOptions],
  );

  const fcfsStatusOptions = useMemo(
    () => [
      ALL_STATUSES_OPTION,
      ...dayOfRequestStatusOptions.filter((option) => FCFS_STATUS_IDS.includes(option.value.dayOfRequestStatusId)),
    ],
    [dayOfRequestStatusOptions],
  );

  const selectedLotteryStatusOption = useMemo(
    () =>
      lotteryStatusId
        ? lotteryStatusOptions.find((val) => val.value?.dayOfRequestStatusId === parseInt(lotteryStatusId))
        : ALL_STATUSES_OPTION,
    [lotteryStatusId, lotteryStatusOptions],
  );

  const selectedFcfsStatusOption = useMemo(
    () =>
      fcfsStatusId
        ? fcfsStatusOptions.find((val) => val.value?.dayOfRequestStatusId === parseInt(fcfsStatusId))
        : ALL_STATUSES_OPTION,
    [fcfsStatusId, fcfsStatusOptions],
  );

  const getDayOfGamesRequestsCall = useCallback(async () => {
    try {
      setRequestsLoading(true);
      const res: DayOfGameRequestDTO[] = await getDayOfRequests(date!, date!, teamId ? Number(teamId) : undefined);
      const data: DayOfTableData[] = res.map((req: DayOfGameRequestDTO) => ({
        dayOfRequestId: req.dayOfRequestId,
        editLink: urlReplace(RouteConstants.ADMIN.TODAYS_TICKETS_EDIT, { requestId: req.dayOfRequestId }),
        firstName: req.user.firstName,
        lastName: req.user.lastName,
        ballparkEmail: req.emailAddress,
        numberOfTickets: req.numberOfTickets,
        game: req.game,
        ticketsSentDisplay: req.ticketSentDisplay,
        status: req.status,
        requestTime: formatSubmittedDate(req.requestedDate),
        statusId: req.statusId,
        stadium: req.game?.venue?.name ?? "",
      }));
      setRequests(data);
    } catch (e) {
      console.error(e);
    } finally {
      setRequestsLoading(false);
    }
  }, [date, teamId]);

  const updateDate = useCallback(
    (value: Date) => {
      setParam("date", formatDate(value, DATE_FORMATTER_STR));
    },
    [setParam],
  );

  const deleteRequest = async (toBeDeleted: DayOfTableData) => {
    try {
      setRequestsLoading(true);
      await deleteDayOfTicketRequest(toBeDeleted.dayOfRequestId);
      setToBeDeleted(undefined);
      await getDayOfGamesRequestsCall();
    } catch (e) {
      console.error(e);
    } finally {
      setRequestsLoading(false);
    }
  };

  const filteredRequests = useMemo(
    () => requests.filter((request) => matchesSearch(request, searchTerm)),
    [requests, searchTerm],
  );

  const lotteryRequests = useMemo(
    () =>
      filteredRequests.filter(
        (request) =>
          LOTTERY_STATUS_IDS.includes(request.statusId) &&
          (!lotteryStatusId || request.statusId === parseInt(lotteryStatusId)),
      ),
    [filteredRequests, lotteryStatusId],
  );

  const fcfsRequests = useMemo(
    () =>
      filteredRequests.filter(
        (request) =>
          FCFS_STATUS_IDS.includes(request.statusId) && (!fcfsStatusId || request.statusId === parseInt(fcfsStatusId)),
      ),
    [filteredRequests, fcfsStatusId],
  );

  useEffect(() => {
    void getDayOfGamesRequestsCall();
  }, [getDayOfGamesRequestsCall]);

  return (
    <div>
      {!!toBeDeleted && (
        <ConfirmationModal
          show={!!toBeDeleted}
          onCancel={() => setToBeDeleted(undefined)}
          onConfirm={() => {
            void deleteRequest(toBeDeleted);
          }}
          header="Delete Today's Ticket Request"
          body={
            <Typography>
              <div className="bcr-mb-2">
                Are you sure you want to delete the request for {toBeDeleted.firstName} {toBeDeleted.lastName} for the
                game
              </div>
              <GameInfo
                gameDate={toBeDeleted.game.gameDate}
                venue={toBeDeleted.game.venue}
                away={toBeDeleted.game.teams.away.team}
                home={toBeDeleted.game.teams.home.team}
                seriesDescription={toBeDeleted.game.seriesDescription}
              />
            </Typography>
          }
          loading={requestsLoading}
        />
      )}
      {!!toBeRevoked && (
        <AdminTicketsRevokeModal
          isOpen={!!toBeRevoked}
          requestId={toBeRevoked.dayOfRequestId}
          onClose={() => {
            setToBeRevoked(undefined);
            void getDayOfGamesRequestsCall();
          }}
        />
      )}
      <Header sticky title="Today's Tickets" flexColumn>
        <Filters actionIconPosition="absolute" numberDisplayed={isMobile ? 1 : 3}>
          <DateInput
            label="Game Date"
            id="todays-tickets-date"
            value={new Date(Date.parse(date!))}
            onChange={updateDate}
            withPortal
            popoverPlacement="bottom-end"
            gutterBottom
            loading={requestsLoading}
          />
          <Select
            id="hometeam-dropdown"
            label="Home Team"
            options={teamsOptions}
            value={selectedHomeTeam}
            onChange={(team: ValueOpt<StatsTeam>) =>
              setParam("teamId", team.value.id ? String(team.value.id) : undefined)
            }
            gutterBottom
            loading={requestsLoading}
          />
          <Search
            id="todays-tickets-search"
            label="Search"
            placeholder="begin typing to filter requests"
            value={searchTerm}
            onChange={(value: string) => setParam("searchTerm", value)}
            gutterBottom
            loading={requestsLoading}
          />
        </Filters>
      </Header>
      <ScrollContainer className="bcr-p-2">
        <Section sectionKey="todays-tickets-lottery" header="Lottery" className="bcr-mb-2">
          <Select
            id="LotteryStatus"
            label="Lottery Status"
            options={lotteryStatusOptions}
            value={selectedLotteryStatusOption}
            onChange={(value: ValueOpt<DayOfRequestStatus | undefined>) =>
              setParam(
                "lotteryStatusId",
                value?.value?.dayOfRequestStatusId ? String(value.value.dayOfRequestStatusId) : undefined,
              )
            }
            gutterBottom
            width={isMobile ? "100%" : "300px"}
            loading={requestsLoading}
          />
          <TodaysTicketsDataTable
            requests={lotteryRequests}
            hasDelete
            hasRevoke
            revokeStatusIds={LOTTERY_REVOKE_STATUS_IDS}
            onDeleteClicked={onDeleteClicked}
            onRevokeClicked={setToBeRevoked}
            loading={requestsLoading}
          />
        </Section>
        <Section sectionKey="todays-tickets-fcfs" header="First Come First Served" className="bcr-mb-2">
          <Select
            id="FcfsRequestStatus"
            label="Request Status"
            options={fcfsStatusOptions}
            value={selectedFcfsStatusOption}
            onChange={(value: ValueOpt<DayOfRequestStatus | undefined>) =>
              setParam(
                "fcfsStatusId",
                value?.value?.dayOfRequestStatusId ? String(value.value.dayOfRequestStatusId) : undefined,
              )
            }
            gutterBottom
            width={isMobile ? "100%" : "300px"}
            loading={requestsLoading}
          />
          <TodaysTicketsDataTable
            requests={fcfsRequests}
            hasDelete
            hasRevoke
            revokeStatusIds={FCFS_REVOKE_STATUS_IDS}
            onDeleteClicked={onDeleteClicked}
            onRevokeClicked={setToBeRevoked}
            loading={requestsLoading}
          />
        </Section>
        <AdminActiveTodaysTicketsTable />
      </ScrollContainer>
    </div>
  );
};

export default AdminTodaysTickets;
